import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SortableTH } from "../components/Table/Header/SortableTH";
import ActionBar from "../components/actionBar/ActionBar";
import FilterDropdown from "../components/filterDropdown/FilterDropdown";
import { SearchFilter } from "../components/filterDropdown/SearchFilter";
import { SimpleButton } from "../components/formComponents/SimpleButton";
import AdoptDeviceModal from "../components/modals/deviceDetailModal/AdoptDeviceModal";
import NameCircle from "../components/nameCircle/NameCircle";
import { useDeviceSettingsContext } from "../context/DeviceSettingsContext";
import { useDevices } from "../hooks/useDevices";
import strings from "../i18n/i18nStrings";
import { TDevice } from "../types/Devices";
import { timeAgo } from "../utils/devices";

const DevicesListPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const deviceId = queryParams.get("device_id");
  const showAddDeviceModal = queryParams.get("add");

  const navigate = useNavigate();
  const goToDevicePage = (device: TDevice) => {
    navigate(`/devices/${device.device_id}`);
  };

  const {
    devices: sortedDevices,
    mutate,
    filters,
    updateFilter,
  } = useDevices();
  const [showAdoptModal, setShowAdoptModal] = useState(false);
  const { onOpenDeviceModal, isDeviceModalOpen } = useDeviceSettingsContext();
  // const [showEditModal, setShowEditModal] = useState(false);
  // const [editThisDeviceId, setEditThisDeviceId] = useState<string>("");

  useEffect(() => {
    if (deviceId) {
      updateFilter({ device_id: deviceId });
    }
  }, [deviceId, updateFilter]);

  useEffect(() => {
    if (showAddDeviceModal == "true") {
      setShowAdoptModal(true);
    }
  }, []);

  useEffect(() => {
    if (!isDeviceModalOpen) {
      mutate();
    }
  }, [isDeviceModalOpen, mutate]);

  const onEditDevice = (device: TDevice) => {
    onOpenDeviceModal(device);
  };

  return (
    <div className="p-0">
      <div className="bg-white shadow rounded-lg mb-14 overflow-x-auto min-w-full">
        <table className="min-w-full">
          <thead>
            <tr className="bg-white">
              <SortableTH
                sortKey={"name"}
                updateFilter={(o) => updateFilter(o)}
                filters={filters}
                classNames="pl-6"
              >
                {strings.deviceSettings.devices(sortedDevices.length)}
              </SortableTH>

              <SortableTH
                sortKey={"last_event_updated_at"}
                updateFilter={(o) => updateFilter(o)}
                filters={filters}
                classNames="pl-8 hidden md:table-cell"
              >
                {strings.deviceSettings.lastUpdate}
              </SortableTH>

              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                {strings.deviceSettings.location}
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                {strings.deviceSettings.deviceNotes}
              </th>
              <th className="sticky right-0 md:bg-white">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {sortedDevices.map((device, idx) => (
              <tr
                key={device.device_id}
                // className="border-gray-200 hover:bg-gray-100 cursor-pointer "
                className="group hover:bg-blue-50 transition-colors duration-250 ease-in-out border-b-2 border-solid md:border-none"
              >
                <td
                  className="px-6 py-4 cursor-pointer whitespace-nowrap"
                  onClick={() => goToDevicePage(device)}
                >
                  <div className="flex items-center">
                    <NameCircle
                      name={device.name || "?"}
                      color={device.color}
                    />
                    <div className="ml-4 text-sm font-bold text-gray-900 flex flex-col gap-2">
                      <span className="md:hidden">
                        {device.name.length > 15 ? `${device.name.substring(0, 15)}...` : device.name}
                      </span>
                      <span className="hidden md:inline">
                        {device.name.length > 23 ? `${device.name.substring(0, 23)}...` : device.name}
                      </span>
                      <small className="text-gray-300">
                        {device.device_id}
                      </small>
                      <span className="md:hidden text-xs">
                        <span className="font-bold uppercase text-gray-500">LAST UPDATED:&nbsp;</span>
                        <span className="pl-8 font-semibold text-gray-900">
                        {(device.last_event_updated_at &&
                          timeAgo(device.last_event_updated_at)) ||
                          "N/a"}
                        </span>
                      </span>
                    </div>
                  </div>
                </td>
                <td
                  className="pl-8 py-4 text-xs font-medium whitespace-nowrap hidden md:table-cell cursor-pointer"
                  onClick={() => onEditDevice(device)}
                >
                  {/* <LocationSelector
                    onChange={(location) => {
                      onUpdateDevice(device.device_id, {
                        location_id: location ? location._id : null,
                      });
                    }}
                    value={device.location_id}
                  /> */}
                  <span
                    title={
                      dayjs(device.last_event_updated_at)?.format(
                        "MMMM D, YYYY h:mm A"
                      ) || "No events recorded"
                    }
                  >
                    {(device.last_event_updated_at &&
                      timeAgo(device.last_event_updated_at)) ||
                      "N/a"}
                  </span>
                </td>
                <td
                  className="py-4 whitespace-nowrap hidden md:table-cell cursor-pointer w-1/6 max-w-0"
                  onClick={() => onEditDevice(device)}
                >
                  <span
                    className={`bg-[${device.location?.color}] text-[${device.location?.color}] px-6 inline-flex text-xs leading-8 font-semibold rounded-full truncate`}
                  >
                    {device.location?.name && device.location.name.length > 20 
                      ? `${device.location.name.substring(0, 20)}...` 
                      : device.location?.name}
                  </span>
                </td>
                <td
                  className="px-6 py-4 text-xs font-medium hidden md:table-cell cursor-pointer w-1/4 max-w-0"
                  onClick={() => onEditDevice(device)}
                >
                  {/* <NoteInput
                    device={device}
                    onSave={(device_id, notes) =>
                      onUpdateDevice(device_id, { notes })
                    }
                  /> */}
                  <div className="line-clamp-2 max-w-xs overflow-hidden">
                    {device.notes}
                  </div>
                </td>
                <td className="pl-3 px-6 py-4 align-top whitespace-nowrap sticky right-0 md:bg-white md:group-hover:bg-blue-50 z-10 md:shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                  <button
                    className="px-3 py-2 text-xs font-bold text-white bg-blue-500 rounded hover:bg-blue-600"
                    onClick={() => onEditDevice(device)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ActionBar>
        <FilterDropdown
          updateFilter={updateFilter}
          isDeviceSettingsFilter 
        />
        <SearchFilter onChange={(value) => updateFilter({ search: value })} />
        {/* <Link
          to="/devices?add=true"
          className="whitespace-nowrap px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white bg-blue-700 hover:bg-blue-800"
        >
          {strings.deviceSettings.getMoreDevices}
        </Link> */}
        <SimpleButton
          onClick={() => {
            setShowAdoptModal(true);
          }}
        >
          
          {strings.deviceSettings.getMoreDevices}
          
        </SimpleButton>
        
      </ActionBar>
      {showAdoptModal && (
        <AdoptDeviceModal onClose={() => setShowAdoptModal(false)} />
      )}
    </div>
  );
};

export default DevicesListPage;
